/**
 * Client wrappers for /api/budget/* endpoints. Thin GETs — server compute
 * is fresh per request, so no caching here either. Mirrors the
 * `dashboard-api.ts` pattern.
 */
import type { ProjectStatus, YmKey } from "@shared/types";

import { apiFetch } from "./api";

export interface BudgetMonth {
  ymKey: YmKey;
  revenue: number;
  cost: number;
  margin: number;
}

export interface BudgetTotals {
  revenue: number;
  cost: number;
  margin: number;
}

export interface BudgetSummaryBlock {
  months: BudgetMonth[];
  totals: BudgetTotals;
}

/**
 * Dual-scope summary: `scoped` honours the optional projectIds filter,
 * `total` always reflects the caller's full visible-project scope. When
 * the request omits projectIds, both blocks carry the same numbers.
 */
export interface BudgetSummaryResponse {
  scoped: BudgetSummaryBlock;
  total: BudgetSummaryBlock;
}

export interface BudgetBreakdownRow {
  projectId: number;
  projectName: string;
  projectCode: string;
  status: ProjectStatus;
  months: BudgetMonth[];
  totals: BudgetTotals;
}

export interface BudgetMemberMonth {
  ymKey: YmKey | "total";
  hours: number;
  revenue: number;
  cost?: number;
  margin?: number;
}

export interface BudgetMemberBreakdownMember {
  memberId: number;
  personId: number | null;
  name: string | null;
  role: string;
  months: BudgetMemberMonth[];
  totals: BudgetMemberMonth;
}

export interface BudgetMemberBreakdownRow {
  projectId: number;
  projectName: string;
  projectCode: string;
  status: ProjectStatus;
  members: BudgetMemberBreakdownMember[];
}

async function getJson<T>(path: string): Promise<T> {
  const res = await apiFetch(path);
  if (!res.ok) throw new Error(`api ${res.status}: ${path}`);
  return (await res.json()) as T;
}

function budgetPath(path: string, projectIds?: number[]): string {
  if (projectIds === undefined) return path;
  return `${path}?projectIds=${projectIds.join(",")}`;
}

export const getBudgetSummary = (projectIds?: number[]): Promise<BudgetSummaryResponse> =>
  getJson<BudgetSummaryResponse>(budgetPath("/api/budget/summary", projectIds));

export const getBudgetBreakdown = (projectIds?: number[]): Promise<BudgetBreakdownRow[]> =>
  getJson<BudgetBreakdownRow[]>(budgetPath("/api/budget/breakdown", projectIds));

export const getBudgetMemberBreakdown = (
  projectIds?: number[],
): Promise<BudgetMemberBreakdownRow[]> =>
  getJson<BudgetMemberBreakdownRow[]>(budgetPath("/api/budget/member-breakdown", projectIds));
